<template>
    <div class="rd-screen" v-if="routine">
        <div class="rd-topbar">
            <v-btn class="rd-back" depressed height="32" @click="goBack">
                <v-icon size="15" class="mr-1">mdi-arrow-left</v-icon>
                {{ $t('common.back') }}
            </v-btn>
            <v-spacer />
            <history-controls v-if="canDuplicate" context="detail" class="mr-2" />
            <actions-menu v-if="isMobile && topbarActions.length" :items="topbarActions" />
            <template v-else-if="!isMobile">
                <v-btn v-if="canEdit" class="btn btn-ghost" :class="{ 'mr-2': !programMode }" depressed height="36"
                    @click="editRoutine">
                    <v-icon size="14" class="mr-1">mdi-pencil</v-icon>
                    {{ $t('common.edit') }}
                </v-btn>
                <v-btn v-if="!programMode" class="btn btn-secondary" depressed height="36" @click="assignRoutine">
                    <v-icon size="15" class="mr-1">mdi-account-plus</v-icon>
                    {{ $t('routines.assign_routine') }}
                </v-btn>
            </template>
        </div>

        <div class="rd-body">
            <div class="rd-left">
                <div class="rd-name">{{ routine.name }}</div>

                <div class="rd-stats">
                    <div class="rd-stat">
                        <div class="rd-stat-lbl">{{ $tc('common.blocks', 2) }}</div>
                        <div class="rd-stat-val">{{ blocksCount }}</div>
                    </div>
                    <div class="rd-stat">
                        <div class="rd-stat-lbl">{{ $t('common.exercises') }}</div>
                        <div class="rd-stat-val">{{ exercisesCount }}</div>
                    </div>
                    <div class="rd-stat">
                        <div class="rd-stat-lbl">{{ $t('common.time') }}</div>
                        <div class="rd-stat-val rd-stat-val--sm">{{ routineDuration }}</div>
                    </div>
                    <div class="rd-stat">
                        <div class="rd-stat-lbl">{{ $t('common.category') }}</div>
                        <div class="rd-stat-val rd-stat-val--sm">{{ routineCategory }}</div>
                    </div>
                </div>

                <div class="rd-metrics">
                    <div class="rd-metric">
                        <div class="rd-metric-val">{{ metrics.series }}</div>
                        <div class="rd-metric-lbl">{{ $tc('exercises.sets', 2) }}</div>
                        <span class="rd-metric-trend">{{ seriesAvg }} {{ averageLabel }}</span>
                    </div>
                    <div class="rd-metric">
                        <div class="rd-metric-val">{{ metrics.reps }}</div>
                        <div class="rd-metric-lbl">{{ $t('exercises.reps') }}</div>
                        <span class="rd-metric-trend">{{ repsAvg }} {{ averageLabel }}</span>
                    </div>
                    <div class="rd-metric">
                        <div class="rd-metric-val">{{ intensityDisplay }}</div>
                        <div class="rd-metric-lbl">{{ $t('common.intensity') }}</div>
                        <span class="rd-metric-trend">{{ averageLabel }}</span>
                    </div>
                </div>

                <load-type-donut :blocks="normalizedBlocks" />
            </div>

            <div class="rd-right">
                <detail-blocks-accordion :sections="blockSections" :empty-text="$t('exercises.no_exercises_set')"
                    :duplicable="canDuplicate" :deletable="canDuplicate" :section-deletable="canDuplicate"
                    :duplicate-section-tooltip="$t('tooltips.duplicate_block')"
                    :duplicate-item-tooltip="$t('tooltips.duplicate_exercise')"
                    :delete-section-tooltip="$t('exercises.delete_block')"
                    :delete-item-tooltip="$t('tooltips.delete_exercise')"
                    @item-click="onItemClick" @duplicate-section="onDuplicateBlock"
                    @duplicate-item="onDuplicateExercise" @delete-item="onDeleteExercise"
                    @delete-section="onDeleteBlock" />
            </div>
        </div>

        <kimsa-confirm ref="confirmDelete" alert-mode :loading="deleting" :close-on-confirm="false"
            :title="$t('common.delete')" :content="deleteConfirmContent" @confirm="onConfirmDelete"
            @cancel="pendingDelete = null" />

        <watch-video-dialog :thumb="currentImg" :video="currentVideo" ref="watchVideo" />
        <routine-editor ref="routineEditor" :coach-id="routine && routine.coachId" @success="onEditSuccess" />
    </div>
</template>

<script>

import { mapActions, mapState } from "vuex";
import { blockUniqueTime, routineLoadMetrics, exerciseSetsLabel, buildRoutineBlocksMeta } from '@/utils/Exercises'
import { db } from '@/utils/Firebase'
import { randomString } from '@/utils/Common'
import WatchVideoDialog from '@/components/common/routines/WatchVideoDialog'
import LoadTypeDonut from '@/components/coach/routines/LoadTypeDonut'
import DetailBlocksAccordion from '@/components/coach/routines/DetailBlocksAccordion'
import RoutineEditor from '@/components/coach/routines/RoutineEditor'
import KimsaConfirm from '@/components/common/KimsaConfirm'
import HistoryControls from '@/components/common/HistoryControls'
import ActionsMenu from '@/components/common/ActionsMenu'
import detailHistoryMixin from '@/mixins/detailHistoryMixin'

export default {
    components: { WatchVideoDialog, LoadTypeDonut, DetailBlocksAccordion, RoutineEditor, KimsaConfirm, HistoryControls, ActionsMenu },
    mixins: [detailHistoryMixin],
    props: {
        routine: {
            type: Object,
            default: () => ({}),
        },
        isThirdDialog: {
            type: Boolean,
            default: false,
        },
        copyRoutine: {
            type: Boolean,
            default: false,
        },
        programMode: {
            type: Boolean,
            default: false,
        }
    },
    data() {
        return {
            normalizedBlocks: [],
            duplicating: false,
            deleting: false,
            pendingDelete: null,
            minutes: 0,
            currentImg: null,
            currentVideo: null,
        }
    },
    computed: {
        ...mapState(['equipment']),
        isOwner(vm = this) {
            return vm.routine?.coachId === vm.currentUser?.uid
        },
        canEdit(vm = this) {
            return vm.isOwner || vm.isCompany
        },
        canDuplicate(vm = this) {
            return vm.canEdit && !vm.programMode && !!vm.routineDocId
        },
        routineDocId(vm = this) {
            return vm.$route.params.uid || vm.routine?.objectID || null
        },
        blocksCount(vm = this) {
            return vm.normalizedBlocks.length
        },
        routineCategory(vm = this) {
            const objective = vm.routine?.description?.objective
            if (!objective) return vm.$t('common.not_available_short')
            const value = typeof objective === 'object' ? (objective.name || objective.label || '') : objective
            return vm.routineObjectiveLabel(value) || value || vm.$t('common.not_available_short')
        },
        averageLabel(vm = this) {
            return vm.$t('common.average').toLowerCase()
        },
        topbarActions(vm = this) {
            const actions = []
            if (vm.canEdit) actions.push({ key: 'edit', label: vm.$t('common.edit'), icon: 'mdi-pencil-outline', action: vm.editRoutine })
            if (!vm.programMode) actions.push({ key: 'assign', label: vm.$t('routines.assign_routine'), icon: 'mdi-account-plus-outline', action: vm.assignRoutine })
            return actions
        },
        metrics(vm = this) {
            return routineLoadMetrics(vm.normalizedBlocks)
        },
        exercisesCount(vm = this) {
            return vm.metrics.exercisesCount
        },
        seriesAvg(vm = this) {
            return vm.exercisesCount ? (vm.metrics.series / vm.exercisesCount).toFixed(1) : '0'
        },
        repsAvg(vm = this) {
            return vm.metrics.series ? (vm.metrics.reps / vm.metrics.series).toFixed(1) : '0'
        },
        intensityDisplay(vm = this) {
            return vm.metrics.intensity != null ? `${vm.metrics.intensity.toFixed(1)}%` : vm.$t('common.not_available_short')
        },
        routineDuration(vm = this) {
            if (!vm.minutes) return vm.$t('common.not_available_short')
            const minutesString = vm.$tc('common.minutes', Number(vm.minutes) === 1 ? 1 : 2).toLowerCase()
            const minutesAdded = Number(vm.minutes * 1.3).toFixed()
            if (Number(vm.minutes) === Number(minutesAdded)) return `${vm.minutes} ${minutesString}`
            return `${vm.minutes} - ${minutesAdded} ${minutesString}`
        },
        deleteConfirmContent(vm = this) {
            if (vm.pendingDelete?.type === 'block') return vm.$t('exercises.confirm_delete_block', { block: vm.pendingDeleteName })
            return vm.$t('common.confirm_delete')
        },
        pendingDeleteName(vm = this) {
            const index = vm.pendingDelete?.sectionIndex
            if (index == null) return ''
            const block = vm.normalizedBlocks[index]
            return block ? vm.blockName(block, index) : ''
        },
        blockSections(vm = this) {
            return vm.normalizedBlocks.map((block, i) => ({
                key: block.uid || i,
                title: vm.blockName(block, i),
                count: block.exercises.length,
                items: block.exercises.map((ex, j) => ({
                    key: ex.uid || j,
                    thumb: vm.exerciseImg(ex),
                    name: ex.name,
                    meta: vm.exerciseMeta(ex, block),
                    video: vm.hasVideo(ex),
                    clickable: vm.hasVideo(ex),
                    raw: ex,
                })),
            }))
        }
    },
    async mounted() {
        const vm = this
        if (vm.equipment.length === 0) await vm.bindEquipment()
        vm.buildBlocks()
        vm.getRoutineMinutes()
        if (vm.canDuplicate) vm.startDetailHistory(() => vm.normalizedBlocks, blocks => vm.persistBlocks(blocks))
    },
    methods: {
        ...mapActions(['bindEquipment']),
        goBack() {
            this.$router.back()
        },
        editRoutine() {
            const vm = this
            if (vm.programMode) return vm.$emit('edit')
            vm.$refs.routineEditor.showEdit({ objectID: vm.$route.params.uid || vm.routine.objectID })
        },
        onEditSuccess() {
            this.$emit('updated')
        },
        async persistBlocks(blocks) {
            const vm = this
            const meta = buildRoutineBlocksMeta(blocks)
            await db.collection('routines').doc(vm.routineDocId).update({
                blocks: meta.blocks,
                'description.blocks': blocks.length,
                'description.equipments': meta.equipments,
            })
            vm.normalizedBlocks = blocks
            vm.getRoutineMinutes()
            vm._ownUpdate = true
            vm.$emit('updated', blocks)
            vm.$nextTick(() => { vm._ownUpdate = false })
        },
        async onDuplicateBlock(section, sectionIndex) {
            const vm = this
            if (!vm.canDuplicate || vm.duplicating) return
            const blocks = JSON.parse(JSON.stringify(vm.normalizedBlocks))
            const source = blocks[sectionIndex]
            if (!source) return
            const copyName = source.name ? `${source.name} ${vm.$t('common.copy_template')}` : null
            blocks.splice(sectionIndex + 1, 0, { ...source, name: copyName, uid: randomString() })
            await vm.withLoading(async () => {
                await vm.persistBlocks(blocks)
                vm.commitDetailHistory(vm.$t('history.duplicate', { name: vm.blockName(source, sectionIndex) }))
            }, 'duplicating')
        },
        async onDuplicateExercise(item, sectionIndex, itemIndex) {
            const vm = this
            if (!vm.canDuplicate || vm.duplicating) return
            const blocks = JSON.parse(JSON.stringify(vm.normalizedBlocks))
            const block = blocks[sectionIndex]
            const exercise = block?.exercises?.[itemIndex]
            if (!exercise) return
            block.exercises.splice(itemIndex + 1, 0, { ...exercise, uid: randomString() })
            await vm.withLoading(async () => {
                await vm.persistBlocks(blocks)
                vm.commitDetailHistory(vm.$t('history.duplicate', { name: `«${exercise.name}»` }))
            }, 'duplicating')
        },
        onDeleteExercise(item, sectionIndex, itemIndex) {
            const vm = this
            if (!vm.canDuplicate) return
            vm.pendingDelete = { type: 'exercise', sectionIndex, itemIndex }
            vm.$refs.confirmDelete.show()
        },
        onDeleteBlock(section, sectionIndex) {
            const vm = this
            if (!vm.canDuplicate) return
            vm.pendingDelete = { type: 'block', sectionIndex }
            vm.$refs.confirmDelete.show()
        },
        async onConfirmDelete() {
            const vm = this
            const pending = vm.pendingDelete
            if (!pending) return
            const blocks = JSON.parse(JSON.stringify(vm.normalizedBlocks))
            let label = ''
            if (pending.type === 'block') {
                if (!blocks[pending.sectionIndex]) return
                label = vm.blockName(blocks[pending.sectionIndex], pending.sectionIndex)
                blocks.splice(pending.sectionIndex, 1)
            } else {
                const block = blocks[pending.sectionIndex]
                if (!block?.exercises?.[pending.itemIndex]) return
                label = `«${block.exercises[pending.itemIndex].name}»`
                block.exercises.splice(pending.itemIndex, 1)
                if (!block.exercises.length) blocks.splice(pending.sectionIndex, 1)
            }
            await vm.withLoading(async () => {
                await vm.persistBlocks(blocks)
                vm.commitDetailHistory(vm.$t('history.delete', { name: label }))
            }, 'deleting')
            vm.pendingDelete = null
            vm.$refs.confirmDelete?.hide()
        },
        assignRoutine() {
            const vm = this
            vm.$emit('selectRoutine', vm.routine)
        },
        buildBlocks() {
            const vm = this
            const raw = vm.routine?.blocks || []
            vm.normalizedBlocks = blockUniqueTime(JSON.parse(JSON.stringify(raw)))
        },
        blockName(block, i) {
            return this.blockNameLabel(block.name) || `${this.$tc('common.blocks', 1)} ${i + 1}`
        },
        exerciseImg(exercise) {
            const vm = this
            if (exercise?.youtubeUrl) return vm.getYoutubeImgUrl(exercise.youtubeUrl)
            return exercise?.img || exercise?.video?.thumb || vm.imgPlaceholder
        },
        hasVideo(exercise) {
            return !!(exercise?.youtubeUrl || exercise?.video?.url)
        },
        exerciseMeta(exercise, block) {
            return exerciseSetsLabel(exercise, block, this.$t)
        },
        onItemClick(item) {
            this.onShowVideo(item.raw)
        },
        onShowVideo(exercise) {
            const vm = this
            if (!vm.hasVideo(exercise)) return
            vm.currentImg = exercise.img || exercise.video?.thumb || null
            const isYoutube = !!exercise?.youtubeUrl
            vm.currentVideo = isYoutube ? exercise.youtubeUrl : exercise.video?.url || null
            if (vm.currentVideo) vm.$refs.watchVideo.show(isYoutube)
        },
        getRoutineMinutes() {
            const vm = this
            let minutes = 0
            vm.normalizedBlocks.forEach(block => {
                block.exercises.forEach(exercise => {
                    minutes += exercise?.config?.sets?.length || block.sets || 0
                })
            })
            vm.minutes = minutes
        }
    },
    watch: {
        routine() {
            const vm = this
            vm.buildBlocks()
            vm.getRoutineMinutes()
            if (!vm._ownUpdate) vm.syncDetailHistory()
        }
    }
}
</script>

<style scoped>
.rd-screen {
    display: flex;
    flex-direction: column;
}

.rd-topbar {
    display: flex;
    align-items: center;
    height: 56px;
    flex-shrink: 0;
    padding: 0 16px;
    border-top: 1px solid var(--v-outline-base);
    border-bottom: 1px solid var(--v-outline-base);
}

.rd-back {
    border-radius: 7px !important;
    border: 1px solid var(--v-outline2-base) !important;
    background: var(--v-surface2-base) !important;
    color: var(--v-txtSecondary-base) !important;
    font-size: 13px;
    font-weight: 500;
    text-transform: none !important;
    letter-spacing: normal !important;
    padding: 0 12px !important;
}

.rd-back:hover {
    color: var(--v-txtPrimary-base) !important;
}

.btn {
    text-transform: none !important;
    letter-spacing: normal !important;
    font-weight: 700;
    border-radius: 8px !important;
    min-width: auto !important;
    padding: 0 16px !important;
}

.btn-ghost {
    background: var(--v-surface2-base) !important;
    color: var(--v-txtSecondary-base) !important;
    border: 1px solid var(--v-outline-base) !important;
}

.btn-ghost:hover {
    color: var(--v-txtPrimary-base) !important;
    border-color: var(--v-outline2-base) !important;
}

.btn-secondary {
    background: linear-gradient(135deg, rgba(204, 255, 51, .15), rgba(51, 153, 153, .15)) !important;
    color: var(--v-primary-base) !important;
    border: 1.5px solid rgba(204, 255, 51, .5) !important;
}

.theme--light .btn-secondary {
    color: var(--v-accent-base) !important;
    border-color: rgba(51, 153, 153, .4) !important;
}

.rd-body {
    display: grid;
    grid-template-columns: 1fr 420px;
    gap: 0;
}

.rd-left {
    padding: 20px 18px;
    border-right: 1px solid var(--v-outline-base);
    display: flex;
    flex-direction: column;
    gap: 14px;
}

.rd-name {
    font-size: 16px;
    font-weight: 700;
    color: var(--v-txtPrimary-base);
    letter-spacing: -.01em;
}

.rd-stats {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 8px;
}

.rd-stat {
    padding: 10px 8px;
    text-align: center;
    background: var(--v-surface-base);
    border: 1px solid var(--v-outline-base);
    border-radius: 10px;
}

.rd-stat-lbl {
    font-size: 9px;
    color: var(--v-txtSecondary-base);
    font-weight: 600;
    margin-bottom: 4px;
    text-transform: uppercase;
    letter-spacing: .04em;
}

.rd-stat-val {
    font-size: 15px;
    font-weight: 700;
    color: var(--v-txtPrimary-base);
}

.rd-stat-val--sm {
    font-size: 11px;
}

.rd-metrics {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 8px;
}

.rd-metric {
    padding: 12px 8px;
    text-align: center;
    background: var(--v-surface-base);
    border: 1px solid var(--v-outline-base);
    border-radius: 10px;
}

.rd-metric-val {
    font-size: 22px;
    font-weight: 800;
    color: var(--v-txtPrimary-base);
    line-height: 1;
}

.rd-metric-lbl {
    font-size: 9px;
    font-weight: 600;
    color: var(--v-txtSecondary-base);
    text-transform: uppercase;
    letter-spacing: .05em;
    margin: 4px 0 6px;
}

.rd-metric-trend {
    font-size: 10px;
    font-weight: 700;
    display: inline-flex;
    align-items: center;
    gap: 2px;
    padding: 2px 6px;
    border-radius: 4px;
    background: rgba(0, 153, 102, .12);
    color: var(--v-success-base);
}

.rd-right {
    padding: 16px;
}

@media (min-width: 961px) {
    .rd-screen {
        height: calc(100vh - 64px);
        overflow: hidden;
    }

    .rd-body {
        flex: 1;
        min-height: 0;
        grid-template-rows: minmax(0, 1fr);
    }

    .rd-left {
        min-height: 0;
        overflow-y: auto;
    }

    .rd-right {
        min-height: 0;
        overflow-y: auto;
    }
}

@media (max-width: 960px) {
    .rd-body {
        grid-template-columns: 1fr;
    }

    .rd-left {
        border-right: none;
        border-bottom: 1px solid var(--v-outline-base);
    }
}
</style>
